import {FormFooterButtons} from 'components/buttons/FormFooterButtons';
import React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {PagingSettings, pagingSettingsSchema} from 'types/PagingSettings';
import selectors from 'redux/settings/selectors';
import Box from '@mui/material/Box';
import LastUpdatedLabel from 'components/LastUpdatedLabel';
import FormikRadioGroup from 'components/inputs/FormikRadioGroup';
import FormikBrowseFileInput from 'components/inputs/FormikBrowseFileInput';
import Grid from '@mui/material/Grid';
import Panel from 'components/Panel';
import FormikDropdown from 'components/inputs/FormikDropdown';
import FormikCheckbox from 'components/inputs/FormikCheckbox';
import {settingsActions} from 'redux/settings';
import FormikTextField from 'components/inputs/FormikTextField';
import AsyncContainer from 'components/AsyncContainer';
import makeStyles from '@mui/styles/makeStyles';
import {DialogTypes} from 'common/names';
import Dialog from 'components/Dialog';
import {useSendData} from 'features/layout/CommonPage/redux/hooks/useSendData';
import {enums} from 'types/enums';
import {useTranslation} from 'react-i18next';
import TestDirectory from 'features/layout/CommonPage/panels/TransportPanel/components/TestDirectory';
import Formik from 'components/hook-form/Formik';
import {useFieldValueGetter} from 'components/hook-form/useFieldValueGetter';

type DialogProps = {
   ok: (values: PagingSettings, callbacks: any) => any;
   cancel: () => void;
};

const Form = ({cancel}: Pick<DialogProps, 'cancel'>) => {
   const {t} = useTranslation();
   const classes = useStyles();
   const getValue = useFieldValueGetter<PagingSettings>();

   const pagingType = getValue('pagingType');
   const useCustomMessage = getValue('useCustomMessage');
   const primaryLocation = getValue('primaryLocation');
   const secondaryLocation = getValue('secondaryLocation');
   const timestamp = getValue('timestamp');
   const lastUser = getValue('lastUser');

   const isEmailType = pagingType === enums.pagertype.enum.EMAIL;
   const isFileType = pagingType === enums.pagertype.enum.FILE;
   const customMessageDisabled =
      pagingType === enums.pagertype.enum.NO_PAGING || (!isFileType && !useCustomMessage);
   return (
      <>
         <Panel name={t('Paging Type')}>
            <FormikRadioGroup autoFocus row name="pagingType" options={enums.pagertype.options} />
         </Panel>
         <FormikDropdown
            className={classes.reportingLevel}
            name="reportingLevel"
            label={t('Minimum level to page')}
            options={enums.reportingLevel.options}
         />
         <Panel name={t('Email')}>
            <FormikTextField
               label={t('Email Address')}
               name="emailAddress"
               disabled={!isEmailType}
            />
            <FormikTextField
               label={t('Email Subject')}
               name="emailSubject"
               disabled={!isEmailType}
            />
            <FormikCheckbox
               name="useCustomMessage"
               label={t('Use Custom Message')}
               disabled={!isEmailType}
            />
         </Panel>
         <Panel name={t('File')}>
            <Grid container alignItems="center" spacing={2}>
               <Grid item xs={10}>
                  <FormikBrowseFileInput
                     name="primaryLocation"
                     placeholder={t('Primary Location')}
                     disabled={!isFileType}
                     directoriesOnly
                  />
               </Grid>
               <Grid item xs={2}>
                  <TestDirectory directoryPath={primaryLocation} disabled={!isFileType} />
               </Grid>
               <Grid item xs={10}>
                  <FormikBrowseFileInput
                     name="secondaryLocation"
                     placeholder={t('Secondary Location')}
                     disabled={!isFileType}
                     directoriesOnly
                  />
               </Grid>
               <Grid item xs={2}>
                  <TestDirectory directoryPath={secondaryLocation} disabled={!isFileType} />
               </Grid>
               <Grid item xs={12}>
                  <FormikTextField label={t('Filename')} name="filename" disabled={!isFileType} />
               </Grid>
            </Grid>
         </Panel>
         <Panel name={t('Custom Message')}>
            <FormikTextField name="pin" label={t('PIN Number')} disabled={customMessageDisabled} />
            <FormikTextField
               name="messageLength"
               label={t('Max Message Length')}
               disabled={customMessageDisabled}
            />
            <FormikTextField
               name="messageTemplate"
               label={t('Message Template')}
               disabled={customMessageDisabled}
               multiline={true}
            />
         </Panel>
         <LastUpdatedLabel date={timestamp} author={lastUser} />
         <Box textAlign={'center'}>
            <FormFooterButtons cancel={cancel} />
         </Box>
      </>
   );
};

const useStyles = makeStyles((theme) => ({
   reportingLevel: {
      marginBottom: theme.spacing(3),
   },
}));

const SettingsPagingForm = ({ok, cancel}: DialogProps) => {
   const settings = useSelector(selectors.pagingSettings);
   const isFetching = useSelector(selectors.isFetching);
   const dispatch = useDispatch();
   React.useEffect(() => {
      dispatch(settingsActions.getPagingSettings());
   }, [dispatch]);

   return (
      <AsyncContainer loading={isFetching}>
         <Formik
            initialValues={settings}
            onSubmit={ok}
            validationSchema={pagingSettingsSchema}
            enableReinitialize
         >
            <Form cancel={cancel} />
         </Formik>
      </AsyncContainer>
   );
};

type Props = {
   onClose: () => void;
};
export const SettingsPagingDialog = ({onClose}: Props) => {
   const sendData = useSendData();
   return (
      <Dialog maxWidth="sm" header={DialogTypes.SettingsPagingDialog.dialogName} close={onClose}>
         <SettingsPagingForm
            ok={(values, callbacks) => {
               sendData(DialogTypes.SettingsPagingDialog.id, values, callbacks);
               onClose();
            }}
            cancel={onClose}
         />
      </Dialog>
   );
};
export default React.memo(SettingsPagingDialog);
